<template>
  <div 
    v-if="show" 
    class="gallery-modal" 
    @click.self="close"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <button class="modal-close" @click="close">&times;</button>
    
    <button 
      v-if="items.length > 1" 
      class="modal-nav prev" 
      @click="prev"
    >
      &#10094;
    </button>
    
    <div class="modal-content" @touchstart="handleTouchStart" @touchend="handleTouchEnd">
      <div v-if="isLoadingImages" class="image-loader">
        <div class="spinner"></div>
      </div>
      
      <transition name="fade" mode="out-in">
        <div v-if="currentItem.type === 'image'" :key="'modal-img-' + index">
          <img :src="currentItem.url" class="modal-image" decoding="async" @load="handleImageLoad" @error="handleImageError" />
        </div>
        <div v-else :key="'modal-vid-' + index">
          <video 
            :src="currentItem.url" 
            class="modal-video" 
            controls 
            autoplay
          >
            Tu navegador no soporta la reproducción de videos.
          </video>
        </div>
      </transition>
      
      <div class="modal-counter">
        <i class="fa-regular fa-image mr-2"></i>
        {{ index + 1 }} / {{ items.length }}
      </div>
    </div>
    
    <button 
      v-if="items.length > 1" 
      class="modal-nav next" 
      @click="next"
    >
      &#10095;
    </button>
  </div>
</template>

<script>
export default {
  name: 'ModalGallery',
  props: {
    show: Boolean,
    items: {
      type: Array,
      default: () => []
    },
    index: {
      type: Number,
      default: 0
    }
  },
  computed: {
    currentItem() {
      if (this.items.length === 0) return { url: '', type: 'image' };
      return this.items[this.index] || { url: '', type: 'image' };
    }
  },
  data() {
    return {
      touchStartX: 0,
      touchEndX: 0,
      minSwipeDistance: 50,
      isLoadingImages: true
    };
  },
  watch: {
    show(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
        this.isLoadingImages = this.currentItem.type === 'image' && Boolean(this.currentItem.url);
      } else {
        document.body.style.overflow = '';
      }
    },
    index() {
      this.isLoadingImages = this.currentItem.type === 'image' && Boolean(this.currentItem.url);
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    prev() {
      this.$emit('prev');
    },
    next() {
      this.$emit('next');
    },
    handleImageLoad() {
      this.isLoadingImages = false;
    },
    handleImageError() {
      this.isLoadingImages = false;
    },
    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    },
    handleTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    },
    handleSwipe() {
      const distance = this.touchEndX - this.touchStartX;
      if (Math.abs(distance) > this.minSwipeDistance) {
        if (distance > 0) {
          this.prev();
        } else {
          this.next();
        }
      }
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.gallery-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.568);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 40px;
  color: white;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 10000;
}

.modal-close:hover {
  color: var(--primary);
}

.modal-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 30px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
}

.modal-nav:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.modal-nav.prev {
  left: 20px;
}

.modal-nav.next {
  right: 20px;
}

.modal-content {
  max-width: 90%;
  max-height: 90%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  position: relative;
}

.modal-image {
  max-width: 95%;
  max-height: 90vh;
  object-fit: contain;
  object-position: center;
  border-radius: 10px;
}

.modal-video {
  max-width: 100%;
  max-height: 80vh;
  border-radius: 10px;
}

.modal-counter {
  background-color: white;
  padding: 5px 20px;
  border-radius: 20px;
  color: black;
  margin-top: 15px;
  font-size: 14px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.image-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 10;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .modal-close {
    top: 10px;
    right: 15px;
    font-size: 32px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 50%;
  }

  .modal-nav {
    display: none;
  }

  .modal-content {
    max-width: 95%;
    max-height: 85%;
    width: 100%;
    touch-action: pan-y;
  }

  .modal-image {
    max-width: 100%;
    max-height: 75vh;
  }

  .modal-video {
    max-width: 100%;
    max-height: 60vh;
  }
}

@media (max-width: 576px) {
  .modal-close {
    top: 8px;
    right: 10px;
    font-size: 28px;
    width: 40px;
    height: 40px;
  }

  .modal-nav {
    display: none;
  }

  .modal-content {
    max-width: 98%;
    max-height: 80%;
    width: 100%;
  }

  .modal-image {
    max-width: 100%;
    max-height: 70vh;
    border-radius: 6px;
  }

  .modal-video {
    max-width: 100%;
    max-height: 50vh;
    border-radius: 6px;
  }

  .modal-counter {
    font-size: 12px;
    margin-top: 10px;
  }
}
</style>
